import {
  configureComposeAssist,
  DiscussionComposeToolbar,
  MentionComposePanel,
  insertTextAtComposeCaret,
  openChatMessageActions,
  renderMentionText,
  resolveChatComposeElement,
  useChatComposeStackOffset,
  useEmailMentionAssist,
  type MentionComposeElement
} from '@jarvis/platform-file-review';
import Chat, { DayHeaderFormat, MessageTimestampFormat } from 'devextreme-react/chat';
import type {
  MessageDeletedEvent,
  MessageEnteredEvent,
  MessageTemplateData,
  MessageUpdatedEvent,
  TypingStartEvent
} from '@jarvis/platform-file-review';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { PATIENT_TRACKING_API_BASE } from '../patientTrackingTypes';
import { PtMentionCandidatePicker } from './PtMentionCandidatePicker';
import { showToast } from '../../notifications/toast';
import { mapPtMessagesToChatItems, type PtDiscussionChatItem } from './ptDiscussionChatItems';
import { applyMentionToken, getMentionPickerQuery } from './ptMentionPickerUtils';
import type {
  PatientTrackingDiscussionMessage,
  PatientTrackingDiscussionTargetType
} from './patientTrackingDiscussionTypes';
import './PatientTrackingDiscussionChat.css';
import './patientTrackingDiscussion.css';

export interface PatientTrackingDiscussionChatProps {
  collapsed?: boolean;
  panelStyle?: CSSProperties;
  messages: PatientTrackingDiscussionMessage[];
  targetType: PatientTrackingDiscussionTargetType;
  targetId: string;
  currentUserId: string;
  currentUserAliases?: Iterable<string>;
  currentUserName: string;
  currentUserHasPhoto?: boolean | null;
  loading?: boolean;
  readOnly?: boolean;
  title?: string;
  onMessageEntered: (text: string) => void | Promise<void>;
  onMessageUpdated?: (messageId: string, text: string) => void | Promise<void>;
  onMessageDeleted?: (messageId: string) => void | Promise<void>;
  onComposeValidationError?: (message: string) => void;
  apiBase?: string;
}

function asChatItem(message: MessageTemplateData['message']): PtDiscussionChatItem | undefined {
  if (!message || typeof message !== 'object') return undefined;
  return message as PtDiscussionChatItem;
}

function runAsyncAction(action: () => void | Promise<void>, errorMessage: string): void {
  void Promise.resolve(action()).catch((err: unknown) => {
    showToast(err instanceof Error ? err.message : errorMessage, 'error');
  });
}

function scrollChatMessageListToBottom(host: HTMLElement | null): void {
  if (!host) return;
  const container = host.querySelector('.dx-chat-messagelist .dx-scrollable-container');
  if (!(container instanceof HTMLElement)) return;
  container.scrollTop = container.scrollHeight;
}

export function PatientTrackingDiscussionChat({
  collapsed = false,
  panelStyle,
  messages,
  targetType,
  targetId,
  currentUserId,
  currentUserAliases = [],
  currentUserName,
  currentUserHasPhoto,
  loading = false,
  readOnly = false,
  title = 'Discussion',
  onMessageEntered,
  onMessageUpdated,
  onMessageDeleted,
  onComposeValidationError,
  apiBase = PATIENT_TRACKING_API_BASE
}: PatientTrackingDiscussionChatProps) {
  const chatHostRef = useRef<HTMLDivElement>(null);
  const [composeInput, setComposeInput] = useState<MentionComposeElement | null>(null);
  const [composeText, setComposeText] = useState('');
  const aliasSet = useMemo(() => {
    const set = new Set<string>();
    for (const value of currentUserAliases) {
      if (value) set.add(value);
    }
    if (currentUserId && currentUserId !== 'unknown') set.add(currentUserId);
    return set;
  }, [currentUserAliases, currentUserId]);
  const identityReady = currentUserId !== 'unknown';
  const canCompose = !readOnly && Boolean(targetId) && identityReady;
  const editingEnabled = Boolean(targetId) && !readOnly && identityReady;
  /** DX scopes the context menu to own (alignment-end) bubbles; boolean true avoids false denials. */
  const editingOptions = useMemo(
    () =>
      editingEnabled
        ? { allowUpdating: true as const, allowDeleting: true as const }
        : { allowUpdating: false as const, allowDeleting: false as const },
    [editingEnabled]
  );
  const { hintVisible, hintMessage, validateBeforeSend } = useEmailMentionAssist(composeInput, {
    enabled: canCompose,
    text: composeText
  });
  const pickerQuery = useMemo(
    () => (canCompose && targetId ? getMentionPickerQuery(composeText) : null),
    [canCompose, composeText, targetId]
  );

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]?.id : null;

  const scrollToLatestMessage = useCallback(() => {
    const run = () => scrollChatMessageListToBottom(chatHostRef.current);
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
      window.setTimeout(run, 60);
    });
  }, []);

  useLayoutEffect(() => {
    if (collapsed || messages.length === 0) return;
    scrollToLatestMessage();
  }, [collapsed, lastMessageId, messages.length, scrollToLatestMessage]);

  const items = useMemo(
    () => mapPtMessagesToChatItems(messages, currentUserId, aliasSet, currentUserHasPhoto),
    [aliasSet, messages, currentUserId, currentUserHasPhoto]
  );

  const selfPhotoUrl =
    currentUserHasPhoto !== false ? '/api/auth/profile/photo' : undefined;

  useChatComposeStackOffset(chatHostRef, !collapsed && editingEnabled && !loading);

  const user = useMemo(
    () => ({
      id: currentUserId,
      name: currentUserName,
      ...(selfPhotoUrl ? { avatarUrl: selfPhotoUrl } : {})
    }),
    [currentUserId, currentUserName, selfPhotoUrl]
  );

  const bindComposeElement = useCallback((el: MentionComposeElement | null) => {
    if (el) {
      configureComposeAssist(el);
      setComposeInput((current) => (current === el ? current : el));
    }
  }, []);

  const onChatInitialized = useCallback(() => {
    bindComposeElement(resolveChatComposeElement(chatHostRef.current));
    const host = chatHostRef.current;
    const box = host?.querySelector('.dx-chat-messagebox');
    if (host && box instanceof HTMLElement) {
      const height = Math.ceil(box.getBoundingClientRect().height);
      if (height > 0) {
        host.style.setProperty('--file-review-chat-compose-stack-height', `${height}px`);
      }
    }
  }, [bindComposeElement]);

  useLayoutEffect(() => {
    if (collapsed || readOnly || !canCompose) return;
    if (composeInput) return;
    const found = resolveChatComposeElement(chatHostRef.current);
    if (found) bindComposeElement(found);
  }, [bindComposeElement, canCompose, collapsed, composeInput, items.length, loading, readOnly]);

  const onTypingStart = useCallback(
    (e: TypingStartEvent) => {
      const target = e.event?.target;
      if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
        bindComposeElement(target);
        setComposeText((current) => (current === target.value ? current : target.value));
      }
    },
    [bindComposeElement]
  );

  useLayoutEffect(() => {
    if (!composeInput || readOnly || !canCompose) return;
    const sync = () => setComposeText((current) => (current === composeInput.value ? current : composeInput.value));
    sync();
    composeInput.addEventListener('input', sync);
    return () => composeInput.removeEventListener('input', sync);
  }, [canCompose, composeInput, readOnly]);

  const onMentionPick = useCallback(
    (token: string) => {
      if (!composeInput || !pickerQuery) return;
      const next = applyMentionToken(composeText, pickerQuery.triggerIndex, token);
      composeInput.value = next;
      composeInput.dispatchEvent(new Event('input', { bubbles: true }));
      setComposeText(next);
    },
    [composeInput, composeText, pickerQuery]
  );

  const onEmojiPick = useCallback(
    (emoji: string) => {
      if (!composeInput) return;
      const next = insertTextAtComposeCaret(composeInput, emoji);
      setComposeText(next);
    },
    [composeInput]
  );

  const onEntered = useCallback(
    (e: MessageEnteredEvent) => {
      const text = e.message?.text?.trim();
      if (!text || readOnly || !canCompose) return;
      const validationError = validateBeforeSend(text);
      if (validationError) {
        onComposeValidationError?.(validationError);
        return;
      }
      void onMessageEntered(text);
    },
    [canCompose, onComposeValidationError, onMessageEntered, readOnly, validateBeforeSend]
  );

  const onUpdated = useCallback(
    (e: MessageUpdatedEvent) => {
      const id = e.message?.id;
      const text = e.text?.trim();
      if (id == null || !text || !onMessageUpdated) return;
      runAsyncAction(() => onMessageUpdated(String(id), text), 'Could not update message.');
    },
    [onMessageUpdated]
  );

  const onDeleted = useCallback(
    (e: MessageDeletedEvent) => {
      const id = e.message?.id;
      if (id == null || !onMessageDeleted) return;
      runAsyncAction(() => onMessageDeleted(String(id)), 'Could not delete message.');
    },
    [onMessageDeleted]
  );

  const messageRender = useCallback((data: MessageTemplateData) => {
    const item = asChatItem(data.message);
    const text =
      item?.text ??
      (data.message && 'text' in data.message ? String(data.message.text ?? '') : '');
    const showActions = Boolean(
      editingEnabled && item?.author?.id && item.author.id === currentUserId
    );
    return (
      <div className="pt-discussion-chat__bubble">
        {showActions ? (
          <button
            type="button"
            className="pt-discussion-chat__bubble-actions"
            aria-label="Edit or delete this message"
            title="Edit or delete"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openChatMessageActions(event.currentTarget);
            }}
          >
            ⋯
          </button>
        ) : null}
        <p className="pt-discussion-chat__bubble-text">{renderMentionText(text)}</p>
      </div>
    );
  }, [currentUserId, editingEnabled]);

  const asideStyle = collapsed ? undefined : panelStyle;

  return (
    <aside
      className={`pt-discussion-chat${collapsed ? ' pt-discussion-chat--collapsed' : ''}`}
      style={asideStyle}
      aria-label={title}
      aria-hidden={collapsed}
    >
      <header className="pt-discussion-chat__header">
        <span className="pt-discussion-chat__header-title">{title}</span>
      </header>
      {loading && items.length === 0 ? (
        <div className="pt-discussion-chat__loading" aria-busy="true">
          <LoadIndicator visible height={24} width={24} />
        </div>
      ) : (
        <div ref={chatHostRef} className="pt-discussion-chat__widget-host">
          <Chat
            className={`pt-discussion-chat__widget${selfPhotoUrl ? ' pt-discussion-chat__widget--self-photo' : ''}`}
            style={
              selfPhotoUrl
                ? ({ '--pt-discussion-chat-self-avatar': `url(${selfPhotoUrl})` } as CSSProperties)
                : undefined
            }
            items={items}
            user={user}
            editing={editingOptions}
            disabled={readOnly && !editingEnabled}
            reloadOnChange={false}
            showAvatar={true}
            showUserName={true}
            showDayHeaders={true}
            showMessageTimestamp={true}
            onMessageEntered={onEntered}
            onInitialized={onChatInitialized}
            onTypingStart={onTypingStart}
            onMessageUpdated={editingEnabled ? onUpdated : undefined}
            onMessageDeleted={editingEnabled ? onDeleted : undefined}
            messageRender={messageRender}
            height="100%"
          >
            <DayHeaderFormat type="shortDate" />
            <MessageTimestampFormat type="shortTime" />
          </Chat>
          {canCompose ? (
            <DiscussionComposeToolbar onEmojiSelect={onEmojiPick} disabled={!composeInput} />
          ) : null}
          <MentionComposePanel
            hintMessage={hintVisible ? hintMessage : null}
            showPicker={Boolean(targetId && pickerQuery)}
            picker={
              targetId && pickerQuery ? (
                <PtMentionCandidatePicker
                  targetType={targetType}
                  targetId={targetId}
                  query={pickerQuery.query}
                  apiBase={apiBase}
                  open
                  onSelect={onMentionPick}
                />
              ) : null
            }
          />
        </div>
      )}
    </aside>
  );
}
